/**
 * /policies/eos/calculation-rules — Session 3c PR 25.
 * Owning agent: Mutaba'aa.
 */

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { session3cApi } from "../lib/session3c-api";
import type { EosCalcRuleItem } from "../lib/session3c-api";
import { useLegalEntityId } from "../lib/legalEntity";
import { PolicyListPage } from "./_PolicyListPage";
import type { PolicyColumn } from "./_PolicyListPage";

export function EosCalculationRulesPolicyPage() {
  const { t } = useTranslation();
  const le = useLegalEntityId();

  const query = useQuery({
    queryKey: ["s3c-eos-calc-rules", le],
    queryFn: () => session3cApi.listEosCalcRules(le ?? ""),
    enabled: !!le,
  });

  const items = query.data?.items ?? [];
  const counts = useMemo(() => ({
    total: items.length,
    attention: 0,
    critical: 0,
  }), [items]);

  const columns: PolicyColumn<EosCalcRuleItem>[] = [
    {
      headerKey: "policies.eos_calc.col.tier",
      label: "Service tier",
      render: (r) => <span className="font-medium text-xs">{r.service_tier}</span>,
    },
    {
      headerKey: "policies.eos_calc.col.multiplier",
      label: "Base multiplier",
      render: (r) => <span className="font-mono">{r.base_multiplier.value}× month/year</span>,
      traceableValue: (r) => ({ label: t("policies.eos_calc.col.multiplier", "Base multiplier"), stack: r.base_multiplier }),
    },
    {
      headerKey: "policies.eos_calc.col.enhancement",
      label: "Enhancement %",
      render: (r) => <span className="font-mono text-xs">{r.enhancement_pct.toFixed(1)}%</span>,
    },
  ];

  return (
    <PolicyListPage<EosCalcRuleItem>
      title={t("policies.eos_calc.title", "EOS Calculation Rules")}
      subtitle={t("policies.eos_calc.subtitle", "Article 84/85 baseline · per-LE enhancement · gratuity overrides. Mutaba'aa vetoes anything below floor.")}
      agent="mutabaa"
      domain="eos_calc"
      newButtonLabel={t("policies.eos_calc.cta.new", "New EOS Rule")}
      newButtonMode="coming_soon"
      newButtonExpectedRelease="M3X.WORKFLOW"
      query={query}
      columns={columns}
      rowKey={(r) => r.id}
      suggestions={[
        {
          headline: t("policies.eos_calc.suggestion.clean_headline", `${items.length} rule${items.length === 1 ? "" : "s"} loaded.`),
          body: t("policies.eos_calc.suggestion.clean_body", "All multipliers at or above Article 84/85 floor (0.5 month/year first 5 yrs, 1.0 month/year thereafter)."),
          primaryActionLabel: t("policies.eos_calc.suggestion.audit", "Open audit log"),
          primaryActionOnClick: () => window.location.assign("/settings/audit-log"),
          suggestionKey: "eos-calc-rules-brief",
        },
      ]}
      briefContext={{
        total: counts.total,
        attention: counts.attention,
        critical: counts.critical,
        top_kind: t("policies.eos_calc.title", "EOS Calculation Rules"),
        window_label: t("policies.eos_calc.window", "by service tier"),
      }}
    />
  );
}
